import { PrinterConnectionType } from '@fedejm/capacitor-esc-pos-printer';
import { EscPosPrinter } from '@/lib/capacitor/EscPosPrinter';
import { Ref, ref, watch } from 'vue';
import { IPrinter } from '@/interfaces/IPrinter';
import {
  IPrintingStrategy,
  IPrintRawBytesParams,
  usePrintingStore,
} from '../use-printing';
import { PrintPluginStatus } from '@/modules/PrintService';
import { vueData } from '@/VueData';
import { AlertType } from '@/interfaces/IAlert';
import { NativePrintError } from '@/lib/print/retry-safety';
import {
  getAutoRetryDecision,
  NATIVE_PRINT_MAX_RETRIES,
  NATIVE_PRINT_RETRY_DELAYS_MS,
} from '@/lib/print/retry-policy';
import { RetrySafety } from '@/interfaces/print-bridge-protocol';
import { usePrintHealthStore } from '@/stores/print-health.store';
import {
  PRINTER_HEALTH_POLL_INTERVAL_MS,
  UnifiedPrinterStatus,
} from '@/lib/print/print-health.utils';

/**
 * USB bulk transfers are more failure-prone with large single writes on some
 * Android devices / hubs / firmware. Chunking reduces timeouts and improves
 * reliability.
 */
const USB_CHUNK_SIZE_BYTES = 1024;

/** Plugin numeric error codes (see PrinterErrorCode in the plugin). */
const PLUGIN_CODE_CONNECT = 1;
const PLUGIN_CODE_NOT_CONNECTED = 2;
const PLUGIN_CODE_SEND = 3;
const PLUGIN_CODE_DEVICE_NOT_FOUND = 6;
const PLUGIN_CODE_STATUS = 7;

/**
 * Native strategy surface: extra methods the shared discovery path
 * (reloadAllPrinters) and the native settings modal need beyond the base
 * strategy contract.
 */
export interface INativePrintingStrategy extends IPrintingStrategy {
  /**
   * Bluetooth listing with prompt control: `passive: true` never triggers
   * the runtime-permission dialog nor the enable prompt (startup paths);
   * interactive paths request normally.
   */
  listBluetoothPrinters(options: { passive: boolean }): Promise<IPrinter[]>;
  /** Requests USB permission for a printer (system dialog). */
  requestUsbPermission(printer: IPrinter): Promise<boolean>;
  /**
   * Live health of ASSIGNED printers, keyed '<type>:<id>' (parity with the
   * bridge's live printer status). Fed by the 30s poller + job outcomes;
   * bluetooth printers get job outcomes ONLY (never flagged offline before
   * the first job — an active probe would fight real jobs and drain
   * battery).
   */
  printerHealth: Ref<Record<string, UnifiedPrinterStatus>>;
  /**
   * TCP reachability probe run THROUGH the printer's serialization chain
   * (probe and job are mutually exclusive in both directions on one-socket
   * printers). Updates the health map — a manual "Probar conexión" can fire
   * the recovery reconcile. Never throws: unreachable/error → reachable
   * false. Never writes supportsDleEot (owned by setNetworkPrinterDleEot).
   */
  probeNetworkPrinter(address: string): Promise<{
    reachable: boolean;
    supportsDleEot: boolean;
    /** Decoded flags when the printer answered DLE EOT (e.g. "PAPER_OUT"). */
    statusDetail?: string[];
  }>;
}

export function isNativePrintingStrategy(
  s: IPrintingStrategy
): s is INativePrintingStrategy {
  return 'listBluetoothPrinters' in s;
}

/** Shape of a Capacitor plugin rejection that carries the numeric code. */
interface IPluginRejection {
  data?: { code?: number };
}

function getPluginErrorCode(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const code = (error as IPluginRejection).data?.code;
  return typeof code === 'number' ? code : undefined;
}

/**
 * Spanish user-facing normalization of plugin error messages (ported from
 * the -pos transport). The returned message also feeds the retry-policy
 * message classification, so the wording of the buckets matters.
 */
function normalizePluginErrorMessage(error: unknown, context: string): string {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
      ? error
      : 'Unknown error';

  const lowerMessage = message.toLowerCase();

  if (
    lowerMessage.includes('bluetooth') &&
    (lowerMessage.includes('disabled') ||
      lowerMessage.includes('not enabled') ||
      lowerMessage.includes('off'))
  ) {
    return 'Bluetooth desactivado';
  }

  if (
    lowerMessage.includes('usb') &&
    (lowerMessage.includes('not available') || lowerMessage.includes('service'))
  ) {
    return 'USB no disponible en este dispositivo';
  }

  if (
    lowerMessage.includes('usb') &&
    lowerMessage.includes('permission not granted')
  ) {
    return 'Permiso USB no concedido';
  }

  if (
    lowerMessage.includes('usb') &&
    (lowerMessage.includes('interface') || lowerMessage.includes('endpoint'))
  ) {
    return 'Dispositivo USB no compatible con impresión';
  }

  if (
    lowerMessage.includes('not found') ||
    lowerMessage.includes('no such device') ||
    lowerMessage.includes('device not found')
  ) {
    return 'Impresora no encontrada';
  }

  if (
    lowerMessage.includes('permission') ||
    lowerMessage.includes('denied') ||
    lowerMessage.includes('not granted')
  ) {
    return 'Permisos no concedidos';
  }

  if (lowerMessage.includes('timeout') || lowerMessage.includes('timed out')) {
    return 'Tiempo de conexión agotado';
  }

  if (
    (lowerMessage.includes('connection') || lowerMessage.includes('connect')) &&
    (lowerMessage.includes('failed') ||
      lowerMessage.includes('lost') ||
      lowerMessage.includes('could not'))
  ) {
    return 'Error de conexión con la impresora';
  }

  if (
    lowerMessage.includes('bulk transfer') &&
    lowerMessage.includes('failed')
  ) {
    // Classified as non-idempotent by retry-policy (never auto-retried).
    return 'Error al enviar datos a la impresora USB';
  }

  return `${context}: ${message}`;
}

/**
 * Retry safety of a failed sendToPrinter call, derived from the plugin's
 * numeric error code. Never maps an unclassified send failure to 'safe':
 * bytes may have left, and an auto-reprint would double-print.
 */
function sendFailureSafety(error: unknown): RetrySafety {
  switch (getPluginErrorCode(error)) {
    case PLUGIN_CODE_CONNECT:
    case PLUGIN_CODE_NOT_CONNECTED:
    case PLUGIN_CODE_DEVICE_NOT_FOUND:
      return 'safe';
    case PLUGIN_CODE_SEND:
    case PLUGIN_CODE_STATUS:
    default:
      return 'unknown';
  }
}

/**
 * Normalized messages that prove the PRINTER is unreachable (health →
 * offline). Deliberately excludes permission / Bluetooth-adapter / USB-stack
 * messages: those are device-side problems, not printer outages.
 */
const CONNECT_CLASS_MESSAGES = [
  'Impresora no encontrada',
  'Error de conexión con la impresora',
  'Tiempo de conexión agotado',
];

/**
 * Whether a FINAL job failure indicates the printer itself is unreachable
 * (connect-class plugin codes or connect-class normalized messages).
 * Send-phase data errors are NOT connect-class — health stays unchanged.
 */
function isConnectClassFailure(error: NativePrintError): boolean {
  switch (getPluginErrorCode(error.cause)) {
    case PLUGIN_CODE_CONNECT:
    case PLUGIN_CODE_NOT_CONNECTED:
    case PLUGIN_CODE_DEVICE_NOT_FOUND:
      return true;
  }
  return CONNECT_CLASS_MESSAGES.some((message) =>
    error.message.startsWith(message)
  );
}

/**
 * Capacitor rejection for a plugin method the installed native build doesn't
 * have (APK bundling plugin < 0.3.0, which predates probeNetworkPrinter).
 * Says nothing about printer reachability — must never map to 'offline'.
 */
function isUnimplementedPluginError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (code === 'UNIMPLEMENTED') return true;
  return (
    typeof message === 'string' && /not implemented|unimplemented/i.test(message)
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function printingStrategyNative(): INativePrintingStrategy {
  const status = ref<PrintPluginStatus>(PrintPluginStatus.Disconnected);
  let printingStore: ReturnType<typeof usePrintingStore> | null = null;

  /** Native handle cache: '<connectionType>:<address>[:<statusCheck>]' -> hashKey. */
  const hashKeyByCacheKey = new Map<string, string>();

  /** Per-printer serialization chains keyed '<type>:<id>'. */
  const printChains = new Map<string, Promise<void>>();

  // #region printer health (bridge-parity: 30s poller + job outcomes)

  /** Assigned-printer health keyed '<type>:<id>' (see interface docs). */
  const printerHealth = ref<Record<string, UnifiedPrinterStatus>>({});

  function setHealth(key: string, status: UnifiedPrinterStatus) {
    if (printerHealth.value[key] === status) return;
    printerHealth.value = { ...printerHealth.value, [key]: status };
    usePrintHealthStore().setPrinterHealthEntry(key, status);
  }

  /** Drops entries for no-longer-assigned printers and mirrors the map. */
  function pruneAndPushHealth(assignedKeys: Set<string>) {
    const next: Record<string, UnifiedPrinterStatus> = {};
    for (const key of Object.keys(printerHealth.value)) {
      if (assignedKeys.has(key)) next[key] = printerHealth.value[key];
    }
    printerHealth.value = next;
    usePrintHealthStore().setPrinterHealthMap(next);
  }

  function healthKeyOf(printer: IPrinter): string {
    return `${printer.type}:${printer.id}`;
  }

  /** Assigned printers (ticket + sticker + per-station comanda), deduped. */
  function getAssignedPrinters(): IPrinter[] {
    if (!printingStore) return [];
    const byKey = new Map<string, IPrinter>();
    const add = (printer: IPrinter | null) => {
      if (printer) byKey.set(healthKeyOf(printer), printer);
    };
    add(printingStore.ticketPrinter);
    add(printingStore.stickerPrinter);
    for (const stationKey of Object.keys(
      printingStore.comandaPrintersByStation
    )) {
      add(printingStore.comandaPrintersByStation[+stationKey]);
    }
    return [...byKey.values()];
  }

  /**
   * One poll sweep over the ASSIGNED printers only:
   * - tcp: reachability probe THROUGH the printer's chain; when the chain is
   *   busy the cycle is SKIPPED (never queued) — a real job already tells us
   *   the outcome, and queued probes would pile up behind a stuck queue;
   * - usb: ONE device enumeration per sweep (present = online); no
   *   permission prompts;
   * - bluetooth: no active probe (job outcomes only).
   */
  async function sweepPrinterHealth(): Promise<void> {
    const assigned = getAssignedPrinters();
    const assignedKeys = new Set(assigned.map(healthKeyOf));
    pruneAndPushHealth(assignedKeys);
    if (!assigned.length) return;

    const tasks: Promise<void>[] = [];

    if (!probeUnsupported) {
      const tcpPrinters = assigned.filter((p) => p.type === 'tcp');
      for (const printer of tcpPrinters) {
        if (printChains.has(healthKeyOf(printer))) continue; // busy: skip cycle
        // probeNetworkPrinter never rejects (see its contract).
        tasks.push(probeNetworkPrinter(String(printer.id)).then(() => undefined));
      }
    }

    const usbPrinters = assigned.filter((p) => p.type === 'usb');
    if (usbPrinters.length) {
      tasks.push(
        (async () => {
          try {
            const result = await EscPosPrinter.getUsbPrinterDevices();
            const presentIds = new Set(result.devices.map((d) => d.id));
            for (const printer of usbPrinters) {
              setHealth(
                healthKeyOf(printer),
                presentIds.has(String(printer.id)) ? 'online' : 'offline'
              );
            }
          } catch {
            // Enumeration failure says nothing about the printers: unchanged.
          }
        })()
      );
    }

    await Promise.allSettled(tasks);
  }

  let healthPollTimer: ReturnType<typeof setInterval> | null = null;
  let sweeping = false;
  /**
   * Old APK (plugin < 0.3.0, no probeNetworkPrinter): once detected, the TCP
   * sweep is disabled for the session — job outcomes remain the only TCP
   * health source there (never a false 'offline' from a missing method).
   */
  let probeUnsupported = false;

  function runHealthSweep() {
    if (sweeping) return;
    sweeping = true;
    void sweepPrinterHealth()
      .catch((e) => console.error('[NativePrint] health sweep failed', e))
      .finally(() => {
        sweeping = false;
      });
  }

  function startHealthPoller() {
    if (healthPollTimer) return;
    // Immediate first sweep: initial status without waiting a full cycle.
    runHealthSweep();
    healthPollTimer = setInterval(runHealthSweep, PRINTER_HEALTH_POLL_INTERVAL_MS);
  }

  function stopHealthPoller() {
    if (!healthPollTimer) return;
    clearInterval(healthPollTimer);
    healthPollTimer = null;
  }

  async function probeNetworkPrinter(address: string): Promise<{
    reachable: boolean;
    supportsDleEot: boolean;
    statusDetail?: string[];
  }> {
    // Chained exactly like a job (two-way exclusivity on one-socket
    // printers): a job arriving mid-probe waits behind it.
    const chainKey = `tcp:${address}`;
    const previous = printChains.get(chainKey) ?? Promise.resolve();
    const probe = previous
      .catch(() => {
        // A failed predecessor must not fail the probe.
      })
      .then(() => EscPosPrinter.probeNetworkPrinter({ address }));

    const chainEntry = probe.then(
      () => undefined,
      () => undefined
    );
    printChains.set(chainKey, chainEntry);
    const cleanupChain = (): void => {
      if (printChains.get(chainKey) === chainEntry) {
        printChains.delete(chainKey);
      }
    };
    void chainEntry.then(cleanupChain, cleanupChain);

    try {
      const result = await probe;
      setHealth(chainKey, result.reachable ? 'online' : 'offline');
      return {
        reachable: result.reachable,
        supportsDleEot: result.reachable && result.supportsDleEot,
        statusDetail: result.statusDetail,
      };
    } catch (err) {
      if (isUnimplementedPluginError(err)) {
        // Old APK without probeNetworkPrinter: says nothing about the
        // printer, so never mark it offline; disable the TCP sweep too.
        probeUnsupported = true;
        return { reachable: false, supportsDleEot: false };
      }
      console.error('[NativePrint] tcp probe failed', err);
      setHealth(chainKey, 'offline');
      return { reachable: false, supportsDleEot: false };
    }
  }

  /** Job outcomes are the native analogue of the bridge's noteJobOutcome. */
  function noteJobOutcome(printer: IPrinter, error: unknown | null) {
    if (error === null) {
      setHealth(healthKeyOf(printer), 'online');
      return;
    }
    if (error instanceof NativePrintError && isConnectClassFailure(error)) {
      setHealth(healthKeyOf(printer), 'offline');
    }
    // Anything else (send-phase data errors, permissions): unchanged.
  }

  // #endregion

  /**
   * Lazy Bluetooth readiness: printing must NEVER be gated on Bluetooth
   * availability globally — a USB/network-only venue that declines the BT
   * dialog must keep printing. Requested on first bluetooth discovery/send;
   * reset on failure so the next attempt re-requests.
   */
  let bluetoothReady: Promise<void> | null = null;

  function ensureBluetoothReady(): Promise<void> {
    if (!bluetoothReady) {
      bluetoothReady = EscPosPrinter.requestBluetoothEnable()
        .then((result) => {
          if (!result.value) {
            throw new NativePrintError('Bluetooth desactivado', 'safe');
          }
        })
        .catch((err) => {
          bluetoothReady = null;
          if (err instanceof NativePrintError) throw err;
          throw new NativePrintError(
            normalizePluginErrorMessage(err, 'Error activando Bluetooth'),
            'safe',
            err
          );
        });
    }
    return bluetoothReady;
  }

  function getConnectionType(type: IPrinter['type']): PrinterConnectionType {
    switch (type) {
      case 'usb':
        return PrinterConnectionType.Usb;
      case 'tcp':
        return PrinterConnectionType.Network;
      case 'bluetooth':
      default:
        return PrinterConnectionType.Bluetooth;
    }
  }

  /**
   * Whether a saved network printer has the post-send DLE EOT status check
   * enabled (set at add-time by probing — see the native settings modal).
   */
  function getNetworkStatusCheck(address: string): boolean {
    const config = printingStore?.networkPrinters?.find(
      (np) => `${np.host}:${np.port}` === address
    );
    return config?.supportsDleEot === true;
  }

  function getCacheKey(type: IPrinter['type'], address: string): string {
    if (type === 'tcp') {
      // statusCheck is part of the native handle's construction, so a toggle
      // must produce a new handle.
      return `network:${address}:${getNetworkStatusCheck(address) ? 1 : 0}`;
    }
    return `${getConnectionType(type)}:${address}`;
  }

  async function ensurePrinterHandle(
    type: IPrinter['type'],
    address: string
  ): Promise<{ hashKey: string; cacheKey: string }> {
    const cacheKey = getCacheKey(type, address);
    const existing = hashKeyByCacheKey.get(cacheKey);
    if (existing) return { hashKey: existing, cacheKey };

    const result = await EscPosPrinter.createPrinter({
      connectionType: getConnectionType(type),
      address,
      ...(type === 'tcp'
        ? { statusCheck: getNetworkStatusCheck(address) }
        : {}),
    });
    hashKeyByCacheKey.set(cacheKey, result.value);
    return { hashKey: result.value, cacheKey };
  }

  async function evictHandle(cacheKey: string): Promise<void> {
    const hashKey = hashKeyByCacheKey.get(cacheKey);
    hashKeyByCacheKey.delete(cacheKey);
    if (!hashKey) return;

    try {
      await EscPosPrinter.disconnectPrinter({ hashKey });
    } catch {
      // best-effort
    }
    try {
      await EscPosPrinter.disposePrinter({ hashKey });
    } catch {
      // best-effort
    }
  }

  /**
   * One physical print attempt. Every failure is thrown as a
   * NativePrintError whose retrySafety comes from the FAILURE PHASE:
   * pre-send steps -> 'safe'; the send itself -> phase table by plugin code.
   */
  async function sendOnce(printer: IPrinter, bytes: number[]): Promise<void> {
    const address = String(printer.id);

    // Pre-send phase: Bluetooth readiness (lazy, never at startup).
    if (printer.type === 'bluetooth') {
      await ensureBluetoothReady();
    }

    // Pre-send phase: native handle.
    let hashKey: string;
    let cacheKey: string;
    try {
      ({ hashKey, cacheKey } = await ensurePrinterHandle(
        printer.type,
        address
      ));
    } catch (err) {
      throw new NativePrintError(
        normalizePluginErrorMessage(err, 'Error creating printer handle'),
        'safe',
        err,
        'presend'
      );
    }

    // Pre-send phase: connect-on-demand — usb/bluetooth ONLY. Network sends
    // are fully self-contained (connect-per-job socket inside the plugin);
    // a pre-probe would add a redundant TCP connection per job.
    if (printer.type !== 'tcp') {
      try {
        const connected = await EscPosPrinter.isPrinterConnected({ hashKey });
        if (!connected.value) {
          await EscPosPrinter.connectPrinter({ hashKey });
        }
      } catch (err) {
        await evictHandle(cacheKey);
        throw new NativePrintError(
          normalizePluginErrorMessage(err, 'Error connecting to printer'),
          'safe',
          err,
          'presend'
        );
      }
    }

    // Send phase. No extra `waitingTime`: the plugin sleeps it AFTER the
    // write and before resolving, so any fixed value becomes a dead gap
    // between consecutive jobs (one per sticker label). USB bulk transfers
    // return once the printer accepted the bytes, the connection stays open
    // between jobs, and the plugin already waits `bytes / 16` ms per
    // usb/bluetooth send. The legacy ThermalPrinter APK never added a fixed
    // wait either.
    try {
      if (printer.type === 'usb') {
        // USB: chunk large payloads to reduce bulk-transfer failures.
        for (
          let offset = 0;
          offset < bytes.length;
          offset += USB_CHUNK_SIZE_BYTES
        ) {
          const end = Math.min(bytes.length, offset + USB_CHUNK_SIZE_BYTES);

          await EscPosPrinter.sendToPrinter({
            hashKey,
            data: bytes.slice(offset, end),
          });
        }
      } else {
        await EscPosPrinter.sendToPrinter({ hashKey, data: bytes });
      }
    } catch (err) {
      // Self-heal: dispose the native handle so the next print recreates a
      // fresh connection (USB bulk-transfer failures are common).
      await evictHandle(cacheKey);
      throw new NativePrintError(
        normalizePluginErrorMessage(err, 'Error sending to printer'),
        sendFailureSafety(err),
        err,
        // 'status' EXCLUSIVELY for the post-send DLE-EOT status check (code
        // 7): bytes were fully written, the printer then reported a problem.
        // Job records render it as "Verificar" instead of "Falló".
        getPluginErrorCode(err) === PLUGIN_CODE_STATUS ? 'status' : 'send'
      );
    }
  }

  /**
   * Bounded retry loop (2.3): phase-'safe' failures whose message classifies
   * as transient are retried up to NATIVE_PRINT_MAX_RETRIES with backoff.
   * Anything non-'safe' is NEVER retried here — bytes may have left.
   */
  async function runJob(printer: IPrinter, bytes: number[]): Promise<void> {
    let attempts = 0;

    for (;;) {
      try {
        await sendOnce(printer, bytes);
        // Job outcome → health: a success flips the printer online instantly
        // (fires the recovery reconcile without waiting for the poller).
        noteJobOutcome(printer, null);
        return;
      } catch (e) {
        const error =
          e instanceof NativePrintError
            ? e
            : new NativePrintError(
                normalizePluginErrorMessage(e, 'Error imprimiendo'),
                'unknown',
                e
              );

        if (error.retrySafety !== 'safe') {
          noteJobOutcome(printer, error);
          throw error;
        }

        const decision = getAutoRetryDecision({
          error,
          attempts,
          maxRetries: NATIVE_PRINT_MAX_RETRIES,
        });
        if (!decision.shouldRetry) {
          noteJobOutcome(printer, error);
          throw error;
        }

        const delayMs =
          NATIVE_PRINT_RETRY_DELAYS_MS[attempts] ??
          NATIVE_PRINT_RETRY_DELAYS_MS[NATIVE_PRINT_RETRY_DELAYS_MS.length - 1];
        console.warn(
          `[NativePrint] Retrying ${printer.name} in ${delayMs}ms (attempt ${
            attempts + 1
          }/${NATIVE_PRINT_MAX_RETRIES}): ${error.message}`
        );
        await delay(delayMs);
        attempts++;
      }
    }
  }

  async function printRawBytes(data: IPrintRawBytesParams): Promise<void> {
    let resolved: IPrinter;
    if (data.printer) {
      resolved = data.printer;
    } else if (data.id && data.type) {
      resolved = { id: data.id, type: data.type, name: String(data.id) };
    } else {
      throw new NativePrintError(
        'If no printer object is provided, id and type are required',
        'safe'
      );
    }
    // const capture: the serialized job closure below must see a narrowed,
    // immutable binding.
    const printer = resolved;

    // Per-printer serialization: one job at a time per printer, different
    // printers run concurrently. In-memory only (no persisted queue).
    const chainKey = `${printer.type}:${printer.id}`;
    const previous = printChains.get(chainKey) ?? Promise.resolve();
    const job = previous
      .catch(() => {
        // A failed predecessor must not fail the whole chain.
      })
      .then(() => runJob(printer, data.bytes));

    printChains.set(chainKey, job);
    // Two-argument then (NOT .finally): .finally would return a new promise
    // that re-rejects with the job's error, firing a spurious global
    // unhandledrejection for every failed job (the caller only awaits `job`).
    const cleanupChain = (): void => {
      if (printChains.get(chainKey) === job) printChains.delete(chainKey);
    };
    void job.then(cleanupChain, cleanupChain);

    return job;
  }

  async function listUsbPrinters(): Promise<IPrinter[]> {
    try {
      const result = await EscPosPrinter.getUsbPrinterDevices();
      // Plugin USB ids are stable ("vendorId:productId:deviceName").
      return result.devices.map((d) => ({
        id: d.id,
        name: d.name,
        type: 'usb' as const,
      }));
    } catch (err) {
      throw new NativePrintError(
        normalizePluginErrorMessage(err, 'Error listando impresoras USB'),
        'safe',
        err
      );
    }
  }

  async function listBluetoothPrinters(options: {
    passive: boolean;
  }): Promise<IPrinter[]> {
    if (options.passive) {
      // Startup paths must never trigger the runtime-permission dialog or
      // the enable prompt: only list when permission is already granted.
      const permissions = await EscPosPrinter.checkPermissions();
      if (permissions.bluetooth !== 'granted') return [];
    } else {
      await ensureBluetoothReady();
    }

    try {
      const result = await EscPosPrinter.getBluetoothPrinterDevices();
      return result.devices.map((d) => ({
        id: d.address,
        name: d.alias ?? d.name ?? d.address,
        type: 'bluetooth' as const,
      }));
    } catch (err) {
      throw new NativePrintError(
        normalizePluginErrorMessage(err, 'Error listando impresoras Bluetooth'),
        'safe',
        err
      );
    }
  }

  async function getPrinters(mode: 'usb' | 'bluetooth'): Promise<IPrinter[]> {
    if (mode === 'bluetooth') {
      return listBluetoothPrinters({ passive: false });
    }
    return listUsbPrinters();
  }

  async function requestUsbPermission(printer: IPrinter): Promise<boolean> {
    try {
      // The plugin reads `address` (the stable USB id), not a deviceId.
      const result = await EscPosPrinter.requestUsbPermission({
        address: String(printer.id),
      });
      return result.value;
    } catch (err) {
      console.warn(
        `[NativePrint] Failed to request USB permission for ${printer.name}:`,
        err
      );
      return false;
    }
  }

  function setStore(store: ReturnType<typeof usePrintingStore>) {
    printingStore = store;

    async function requestPermissionsSafe(printer: IPrinter) {
      try {
        if (printer.type === 'usb') {
          await requestUsbPermission(printer);
        } else if (printer.type === 'bluetooth') {
          await ensureBluetoothReady();
        }
        // tcp: nothing to request (INTERNET is install-time).
      } catch (e) {
        vueData.addAlert(
          'Error obteniendo permisos para imprimir de ' + printer.name,
          AlertType.Error
        );
        console.error(e);
      }
    }

    const printerKeys = ['ticketPrinter', 'stickerPrinter'] as const;

    for (const key of printerKeys) {
      watch(
        () => store[key],
        async (printer, old) => {
          if (printer && printer.id !== old?.id) {
            await requestPermissionsSafe(printer);
          }
        },
        { immediate: true }
      );
    }

    // Comanda printers are keyed by print station: request permissions for
    // each newly assigned printer.
    watch(
      () => store.comandaPrintersByStation,
      async (byStation, oldByStation) => {
        for (const key of Object.keys(byStation ?? {})) {
          const printer = byStation[+key];
          const old = oldByStation?.[+key];

          if (printer && printer.id !== old?.id) {
            await requestPermissionsSafe(printer);
          }
        }
      },
      { immediate: true, deep: true }
    );

    // A statusCheck/host edit invalidates cached network handles (the flag is
    // baked into the native handle): evict them so the next job recreates.
    watch(
      () => store.networkPrinters,
      async () => {
        const networkKeys = [...hashKeyByCacheKey.keys()].filter((k) =>
          k.startsWith('network:')
        );
        for (const key of networkKeys) {
          await evictHandle(key);
        }
      },
      { deep: true }
    );
  }

  function connect() {
    // Connected immediately and unconditionally: printing must NEVER be
    // gated on Bluetooth availability (a USB/network-only venue that
    // declines the BT dialog would lose ALL printing — the status watcher
    // and auto-print paths bail unless status is Connected). Bluetooth
    // unavailability surfaces per-send as a printer-level error.
    status.value = PrintPluginStatus.Connected;
    startHealthPoller();
  }

  async function disconnect() {
    stopHealthPoller();
    const cacheKeys = [...hashKeyByCacheKey.keys()];
    for (const cacheKey of cacheKeys) {
      await evictHandle(cacheKey);
    }
    status.value = PrintPluginStatus.Disconnected;
  }

  return {
    getPrinters,
    listBluetoothPrinters,
    requestUsbPermission,
    printerHealth,
    probeNetworkPrinter,
    printRawBytes,
    status,
    setStore,
    connect,
    disconnect: () => void disconnect(),
  };
}
